import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MaterialsUsed = () => {
  const { t, language } = useLanguage();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [data, searchTerm]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/materials-used`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...data];

    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.prabhag_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.seat_no?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredData(filtered);
  };

  if (loading) {
    return <div className={`text-center py-12 ${language === 'mr' ? 'marathi-text' : ''}`}>{t('loading')}</div>;
  }

  return (
    <div className="space-y-6" data-testid="materials-used-page">
      <div>
        <h1 className={`text-3xl font-bold text-primary mb-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
          {t('materialsUsed')}
        </h1>
        <p className={`text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
          {language === 'en' ? 'Statement 10: Election materials inventory' : 'विधान 10: निवडणूक साहित्य सूची'}
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={language === 'en' ? 'Search by seat or prabhag...' : 'जागा किंवा प्रभागानुसार शोधा...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className={language === 'mr' ? 'marathi-text' : ''}>
            {filteredData.length} {language === 'en' ? 'Records' : 'रेकॉर्ड'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{language === 'en' ? 'Seat' : 'जागा'}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{language === 'en' ? 'Prabhag' : 'प्रभाग'}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('reservation')}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('pollingStations')}</TableHead>
                  <TableHead>CU</TableHead>
                  <TableHead>BU</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{language === 'en' ? 'Memory Packs' : 'मेमरी पॅक'}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{language === 'en' ? 'Power Packs' : 'पावर पॅक'}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{language === 'en' ? 'Marker Pens' : 'मार्कर'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.seat_no}</TableCell>
                      <TableCell>{item.prabhag_no}</TableCell>
                      <TableCell>{item.reservation}</TableCell>
                      <TableCell className="font-semibold">{item.polling_stations}</TableCell>
                      <TableCell>{item.cu}</TableCell>
                      <TableCell>{item.bu}</TableCell>
                      <TableCell>{item.memory_packs}</TableCell>
                      <TableCell>{item.power_packs}</TableCell>
                      <TableCell>{item.marker_pens}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <div className={language === 'mr' ? 'marathi-text' : ''}>{t('noData')}</div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MaterialsUsed;